/**
 * SearchAutocomplete Component
 *
 * A reusable autocomplete input component with debounced search.
 * Shows a dropdown with search results and allows selection.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface SearchAutocompleteOption {
  id: string;
  label: string;
  sublabel?: string;
}

export interface SearchAutocompleteProps<T extends SearchAutocompleteOption = SearchAutocompleteOption> {
  /** Label for the input */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Currently selected option */
  value: T | null;
  /** Callback when selection changes */
  onChange: (option: T | null) => void;
  /** Options to display in dropdown */
  options: T[];
  /** Whether options are loading */
  isLoading?: boolean;
  /** Search input value (controlled externally) */
  searchValue: string;
  /** Callback when search input changes */
  onSearchChange: (value: string) => void;
  /** Error message */
  error?: string;
  /** Helper text */
  helperText?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
  /** Empty state message */
  emptyMessage?: string;
  /** Minimum search length to show results */
  minSearchLength?: number;
  /** Custom option renderer */
  renderOption?: (option: T, isHighlighted: boolean) => React.ReactNode;
  /** Keep focus on input after selection (useful for quick successive selections) */
  keepFocusOnSelect?: boolean;
}

export function SearchAutocomplete<T extends SearchAutocompleteOption = SearchAutocompleteOption>({
  label,
  placeholder = 'Rechercher...',
  value,
  onChange,
  options,
  isLoading = false,
  searchValue,
  onSearchChange,
  error,
  helperText,
  disabled = false,
  className,
  emptyMessage = 'Aucun resultat',
  minSearchLength = 2,
  renderOption,
  keepFocusOnSelect = false,
}: SearchAutocompleteProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const justSelectedRef = useRef(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset highlighted index when options change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [options]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onSearchChange(newValue);
    setIsOpen(true);
  }, [onSearchChange]);

  const handleInputFocus = useCallback(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    if (searchValue.length >= minSearchLength || options.length > 0) {
      setIsOpen(true);
    }
  }, [searchValue, minSearchLength, options.length]);

  const handleOptionSelect = useCallback((option: T) => {
    onChange(option);
    onSearchChange('');
    setIsOpen(false);
    if (keepFocusOnSelect) {
      justSelectedRef.current = true;
      inputRef.current?.focus();
    }
  }, [onChange, onSearchChange, keepFocusOnSelect]);

  const handleClear = useCallback(() => {
    onChange(null);
    onSearchChange('');
    inputRef.current?.focus();
  }, [onChange, onSearchChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
          handleOptionSelect(options[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  }, [isOpen, options, highlightedIndex, handleOptionSelect]);

  const showDropdown = isOpen && (
    searchValue.length >= minSearchLength ||
    isLoading ||
    options.length > 0
  );

  const inputId = label?.toLowerCase().replace(/\s+/g, '-');

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-dark-500 mb-1.5"
        >
          {label}
        </label>
      )}

      <div className="relative">
        {/* Selected value display */}
        {value ? (
          <div
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-lg border bg-white px-3 py-2 text-sm',
              disabled
                ? 'cursor-not-allowed bg-neutral-100 text-neutral-400'
                : 'border-neutral-300',
              error && 'border-danger-500'
            )}
          >
            <div className="flex-1 truncate">
              <span className="text-dark-500">{value.label}</span>
              {value.sublabel && (
                <span className="ml-2 text-neutral-400">{value.sublabel}</span>
              )}
            </div>
            {!disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="ml-2 p-1 hover:bg-neutral-100 rounded-md transition-colors"
                aria-label="Effacer la selection"
              >
                <X className="w-4 h-4 text-neutral-500" />
              </button>
            )}
          </div>
        ) : (
          /* Search input */
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              ref={inputRef}
              id={inputId}
              type="text"
              value={searchValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              className={cn(
                'flex h-10 w-full rounded-lg border bg-white pl-10 pr-3 py-2 text-sm transition-colors',
                'placeholder:text-neutral-400',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500',
                'disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400',
                error
                  ? 'border-danger-500 focus-visible:ring-danger-500 focus-visible:border-danger-500'
                  : 'border-neutral-300 hover:border-neutral-400'
              )}
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
              aria-expanded={showDropdown}
              aria-autocomplete="list"
              role="combobox"
            />
            {isLoading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 animate-spin" />
            )}
          </div>
        )}

        {/* Dropdown */}
        {showDropdown && !value && (
          <div
            className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-neutral-200 shadow-lg max-h-60 overflow-auto"
            role="listbox"
          >
            {isLoading ? (
              <div className="px-4 py-3 text-sm text-neutral-500 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Recherche en cours...
              </div>
            ) : options.length === 0 ? (
              <div className="px-4 py-3 text-sm text-neutral-500">
                {searchValue.length < minSearchLength
                  ? `Saisissez au moins ${minSearchLength} caracteres`
                  : emptyMessage}
              </div>
            ) : (
              options.map((option, index) => (
                <button
                  key={option.id}
                  type="button"
                  className={cn(
                    'w-full px-4 py-2 text-left text-sm hover:bg-neutral-50 transition-colors',
                    'focus:outline-none focus:bg-neutral-50',
                    highlightedIndex === index && 'bg-neutral-50'
                  )}
                  onClick={() => handleOptionSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  role="option"
                  aria-selected={highlightedIndex === index}
                >
                  {renderOption ? (
                    renderOption(option, highlightedIndex === index)
                  ) : (
                    <>
                      <div className="text-dark-500">{option.label}</div>
                      {option.sublabel && (
                        <div className="text-xs text-neutral-400">{option.sublabel}</div>
                      )}
                    </>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {error && (
        <p id={`${inputId}-error`} className="mt-1.5 text-sm text-danger-500">
          {error}
        </p>
      )}
      {helperText && !error && (
        <p id={`${inputId}-helper`} className="mt-1.5 text-sm text-neutral-500">
          {helperText}
        </p>
      )}
    </div>
  );
}
